import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../lib/apiClient';
import type { LotChatLink, LotDetailResponse } from '../lib/lots';

const DETAIL_REFETCH_MS = 5_000;
const DETAIL_STALE_MS = 3_000;
const CHAT_STALE_MS = 60_000;

export function useLotDetail(lotId: string): UseQueryResult<LotDetailResponse> {
  return useQuery({
    queryKey: ['lot', lotId],
    queryFn: ({ signal }) => api.get<LotDetailResponse>(`/api/lots/${lotId}`, { signal }),
    staleTime: DETAIL_STALE_MS,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && data.lot.status === 'closed') return false;
      return DETAIL_REFETCH_MS;
    },
  });
}

export function useLotChatLink(
  lotId: string,
  enabled: boolean,
): UseQueryResult<LotChatLink | null> {
  return useQuery({
    queryKey: ['lot', lotId, 'chat'],
    enabled,
    staleTime: CHAT_STALE_MS,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
    queryFn: async ({ signal }) => {
      try {
        return await api.get<LotChatLink>(`/api/lots/${lotId}/chat`, { signal });
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
}
